/**
 * FinancialSummaryWidget (9.9b)
 * Displays financial metrics: proposals, contracts by category, revenue
 */
import { useFinancialSummary, FinancialSummaryData } from '@/hooks/useDashboardReports';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DollarSign, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';

// Format currency helper
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function FinancialSummaryWidget() {
  const { data, isLoading, error } = useFinancialSummary();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Financial Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 animate-pulse">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Financial Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load financial data</p>
        </CardContent>
      </Card>
    );
  }

  const financialData = data as FinancialSummaryData;

  return (
    <Card data-testid="financial-summary-widget">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <DollarSign className="h-5 w-5" />
          Financial Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Proposals Outstanding */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Proposals Outstanding</h4>
          <div className="flex justify-between items-baseline">
            <span className="text-2xl font-bold">{financialData.proposals.count}</span>
            <span className="text-lg text-muted-foreground">
              {formatCurrency(financialData.proposals.total_value)}
            </span>
          </div>
        </div>

        {/* Contract Value by Category */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Contract Value</h4>
          <div className="space-y-2">
            {Object.entries(financialData.contracts).map(([category, stats]) => (
              <div key={category} className="flex justify-between items-baseline text-sm">
                <span className="capitalize">{category.replace(/_/g, ' ')}</span>
                <div className="text-right">
                  {/* v0.12.27 Item 4: pluralize "contract" only when count !== 1 */}
                  <span className="font-semibold">{stats.count} {stats.count === 1 ? 'contract' : 'contracts'}</span>
                  <span className="text-muted-foreground ml-2">
                    {formatCurrency(stats.total_value)}
                  </span>
                </div>
              </div>
            ))}
            {Object.keys(financialData.contracts).length === 0 && (
              <p className="text-sm text-muted-foreground">No contracts</p>
            )}
          </div>
        </div>

        {/* Revenue Metrics */}
        <div>
          <h4 className="text-sm font-medium text-muted-foreground mb-2">Revenue</h4>
          <TooltipProvider>
            <div className="grid grid-cols-2 gap-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="p-2 rounded hover:bg-gray-50 cursor-default">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      Billed (YTD) <Info size={12} className="text-gray-400" aria-hidden="true" />
                    </p>
                    <p className="text-lg font-semibold">{formatCurrency(financialData.revenue.billed_ytd)}</p>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p>Sum of all invoices created this calendar year (Jan 1 to today), regardless of payment status.</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="p-2 rounded hover:bg-gray-50 cursor-default">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      Collected (All Time) <Info size={12} className="text-gray-400" aria-hidden="true" />
                    </p>
                    <p className="text-lg font-semibold">{formatCurrency(financialData.revenue.collected_all_time)}</p>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p>Sum of all payments received across all invoices ever. Only includes invoices with status "paid".</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="p-2 rounded hover:bg-gray-50 cursor-default">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      Outstanding <Info size={12} className="text-gray-400" aria-hidden="true" />
                    </p>
                    <p className="text-lg font-semibold text-orange-600">
                      {formatCurrency(financialData.revenue.outstanding_all_time)}
                    </p>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p>Sum of all invoices with status "sent" that are awaiting payment. Excludes drafts and paid invoices.</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="p-2 rounded hover:bg-gray-50 cursor-default">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      Billed (All Time) <Info size={12} className="text-gray-400" aria-hidden="true" />
                    </p>
                    <p className="text-lg font-semibold">{formatCurrency(financialData.revenue.billed_all_time)}</p>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p>Sum of all invoices ever created, regardless of date or payment status.</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>
      </CardContent>
    </Card>
  );
}
